<template>
  <div class="relative w-full">
    <input
      type="text"
      v-model="query"
      ref="inputRef"
      @input="onInput"
      @focus="handleFocus"
      @blur="hideSuggestions"
      placeholder="Add Destination or Region"
      class="w-full h-12 border border-gray-200 rounded-full px-4 pr-12 shadow-sm focus:ring-2 focus:ring-[#4ECF84]/40 focus:border-[#4ECF84] outline-none transition-colors text-sm md:text-sm placeholder:text-base md:placeholder:text-sm placeholder:text-gray-300"
    />
    
    <!-- Pin Icon / Clear Button -->
    <button
      type="button"
      class="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-black hover:text-[#4ECF84] transition-colors"
      @click="query ? clearLocation() : focusInput()"
      :disabled="loading"
      :title="query ? 'Clear location' : 'Enter destination'"
    >
      <!-- Clear X Icon when there's content -->
      <svg v-if="query && !loading" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
      <!-- Pin Icon when empty -->
      <svg v-else-if="!loading" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
      </svg>
      <!-- Loading Spinner -->
      <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </button>

    <!-- Suggestions Dropdown -->
    <ul
      v-if="showSuggestions && (suggestions.length || (!query.trim() && popularRegions.length))"
      :class="[
        isDesktop ? 'fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[60] box-border' : 'absolute z-[70] bg-white border border-gray-200 mt-1 rounded-lg shadow-lg w-full max-h-60 overflow-auto'
      ]"
      :style="dropdownStyle"
    >
      <!-- Show popular regions when field is empty -->
      <template v-if="!query.trim()">
        <li class="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200">
          Popular Regions
        </li>
        <li
          v-for="region in popularRegions"
          :key="region.name"
          @mousedown="selectRegion(region)"
          class="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
        >
          <div class="font-normal text-black">{{ region.name }}</div>
          <div class="text-sm text-gray-500">{{ region.description }}</div>
        </li>
      </template>
      
      <!-- Show search results when typing -->
      <template v-else>
        <li
          v-for="(suggestion, index) in suggestions"
          :key="suggestion.place_id || suggestion.name"
          @mousedown="selectSuggestion(suggestion)"
          class="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
        >
          <div class="font-normal text-black">{{ suggestion.structured_formatting?.main_text || suggestion.description || suggestion.name }}</div>
          <div v-if="suggestion.structured_formatting?.secondary_text || suggestion.description" class="text-sm text-gray-500">
            {{ suggestion.structured_formatting?.secondary_text || suggestion.description }}
          </div>
        </li>
      </template>
    </ul>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { loadGoogleMapsAPI } from '@/utils/googleMapsLoader'

// Define emits
const emit = defineEmits(['select'])

// Reactive data
const query = ref('')
const suggestions = ref([])
const showSuggestions = ref(false)
const loading = ref(false)
const inputRef = ref(null)

// Popular regions from CMS (falls back to hardcoded values until CMS responds)
import { usePopularRegions } from '@/composables/useCms'
const { regions: popularRegions } = usePopularRegions()
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
const onResize = () => { windowWidth.value = typeof window !== 'undefined' ? window.innerWidth : 0 }

const isDesktop = computed(() => windowWidth.value >= 768)
const dropdownStyle = computed(() => {
  // depend on windowWidth so it recalculates on resize
  const _w = windowWidth.value
  if (!showSuggestions.value) return {}
  if (!isDesktop.value) return {}
  const el = inputRef.value
  if (!el) return {}
  const rect = el.getBoundingClientRect()
  // Input uses focus:ring-2 when active. Adjust dropdown to include ring width so they match.
  const ringPx = 2
  const width = rect.width + ringPx * 2
  return {
    top: `${rect.bottom + 10}px`,
    left: `${rect.left - ringPx}px`,
    width: `${width}px`
  }
})

// Google Maps services
let sessionToken = null

// Initialize Google Maps services
const initServices = () => {
  if (window.google?.maps?.places) {
    if (!sessionToken) {
      sessionToken = new google.maps.places.AutocompleteSessionToken()
    }
    return true
  }
  return false
}

// Handle input changes
const onInput = async () => {
  console.log('InternationalLocation onInput called with query:', query.value)
  
  if (!query.value.trim()) {
    suggestions.value = []
    showSuggestions.value = false
    return
  }

  // Emit the current input value as a manual selection to allow the parent to validate
  emit('select', {
    description: query.value,
    name: query.value,
    formatted_address: query.value,
    place_id: null,
    manual_entry: true
  })

  if (!initServices()) {
    console.warn('Google Maps API not loaded in InternationalLocation')
    return
  }

  console.log('Starting autocomplete search for:', query.value)
  loading.value = true
  showSuggestions.value = true

  try {
    // First, check if the query matches any of our popular regions
    const matchingRegions = popularRegions.filter(region => 
      region.name.toLowerCase().includes(query.value.toLowerCase()) ||
      query.value.toLowerCase().includes(region.name.toLowerCase())
    )

    // Try the new AutocompleteSuggestion API first
    if (window.google?.maps?.places?.AutocompleteSuggestion) {
      console.log('Using new AutocompleteSuggestion API')
      const request = {
        input: query.value,
        sessionToken: sessionToken,
        includedPrimaryTypes: [
          'country', 
          'administrative_area_level_1', 
          'administrative_area_level_2',
          'locality', 
          'sublocality', 
          'establishment', 
          'tourist_attraction',
          'natural_feature', // For geographical regions
          'political' // For political regions
        ],
        locationBias: {
          radius: 50000000, // Global search
          center: { lat: 0, lng: 0 }
        }
      }

      const { suggestions: predictions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      
      // Combine matching regions with Google Maps results
      let combinedSuggestions = []
      
      // Add matching popular regions first (higher priority)
      if (matchingRegions.length > 0) {
        combinedSuggestions = matchingRegions.map(region => ({
          place_id: `region_${region.name.toLowerCase().replace(/\s+/g, '_')}`,
          description: region.name,
          name: region.name,
          structured_formatting: {
            main_text: region.name,
            secondary_text: region.description
          },
          is_region: true,
          region_type: 'popular'
        }))
      }
      
      // Add Google Maps results
      if (predictions && predictions.length > 0) {
        const googleSuggestions = predictions.map(prediction => ({
          place_id: prediction.placePrediction.placeId,
          description: prediction.placePrediction.text.text,
          structured_formatting: {
            main_text: prediction.placePrediction.structuredFormat?.mainText?.text || prediction.placePrediction.text.text,
            secondary_text: prediction.placePrediction.structuredFormat?.secondaryText?.text || ''
          }
        }))
        combinedSuggestions = [...combinedSuggestions, ...googleSuggestions]
      }
      
      suggestions.value = combinedSuggestions
      console.log('New API - Combined suggestions:', suggestions.value)
      
      if (suggestions.value.length === 0) {
        console.log('New API - No predictions returned')
      }
    } else {
      // Fallback to older AutocompleteService API
      console.log('Falling back to AutocompleteService API')
      const service = new google.maps.places.AutocompleteService()
      
      const request = {
        input: query.value,
        sessionToken: sessionToken,
        types: ['(regions)'], // Include regions, cities, and countries
        componentRestrictions: {} // No country restrictions for international search
      }

      service.getPlacePredictions(request, (predictions, status) => {
        loading.value = false
        
        // Combine matching regions with Google Maps results
        let combinedSuggestions = []
        
        // Add matching popular regions first (higher priority)
        if (matchingRegions.length > 0) {
          combinedSuggestions = matchingRegions.map(region => ({
            place_id: `region_${region.name.toLowerCase().replace(/\s+/g, '_')}`,
            description: region.name,
            name: region.name,
            structured_formatting: {
              main_text: region.name,
              secondary_text: region.description
            },
            is_region: true,
            region_type: 'popular'
          }))
        }
        
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const googleSuggestions = predictions.map(prediction => ({
            place_id: prediction.place_id,
            description: prediction.description,
            structured_formatting: {
              main_text: prediction.structured_formatting?.main_text || prediction.description,
              secondary_text: prediction.structured_formatting?.secondary_text || ''
            }
          }))
          combinedSuggestions = [...combinedSuggestions, ...googleSuggestions]
        }
        
        suggestions.value = combinedSuggestions
        console.log('Fallback API - Combined suggestions:', suggestions.value)
        
        if (suggestions.value.length === 0) {
          console.log('Fallback API - No predictions or error:', status)
        }
      })
      return // Exit early since callback handles the response
    }
    
    loading.value = false
  } catch (error) {
    loading.value = false
    console.error('Error fetching place predictions in InternationalLocation:', error)
    
    // Try fallback to AutocompleteService if new API fails
    try {
      console.log('Trying fallback AutocompleteService due to error')
      const service = new google.maps.places.AutocompleteService()
      
      const request = {
        input: query.value,
        sessionToken: sessionToken,
        types: ['(regions)'],
        componentRestrictions: {}
      }

      service.getPlacePredictions(request, (predictions, status) => {
        // Combine matching regions with Google Maps results
        let combinedSuggestions = []
        
        // Add matching popular regions first (higher priority)  
        const matchingRegions = popularRegions.filter(region => 
          region.name.toLowerCase().includes(query.value.toLowerCase()) ||
          query.value.toLowerCase().includes(region.name.toLowerCase())
        )
        
        if (matchingRegions.length > 0) {
          combinedSuggestions = matchingRegions.map(region => ({
            place_id: `region_${region.name.toLowerCase().replace(/\s+/g, '_')}`,
            description: region.name,
            name: region.name,
            structured_formatting: {
              main_text: region.name,
              secondary_text: region.description
            },
            is_region: true,
            region_type: 'popular'
          }))
        }
        
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const googleSuggestions = predictions.map(prediction => ({
            place_id: prediction.place_id,
            description: prediction.description,
            structured_formatting: {
              main_text: prediction.structured_formatting?.main_text || prediction.description,
              secondary_text: prediction.structured_formatting?.secondary_text || ''
            }
          }))
          combinedSuggestions = [...combinedSuggestions, ...googleSuggestions]
        }
        
        suggestions.value = combinedSuggestions
        console.log('Error fallback - Combined suggestions:', suggestions.value)
        
        if (suggestions.value.length === 0) {
          console.log('Error fallback - Failed:', status)
        }
      })
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError)
      suggestions.value = []
    }
  }
}

// Select a popular region
const selectRegion = (region) => {
  query.value = region.name
  showSuggestions.value = false
  
  // Emit the select event with region data
  emit('select', {
    place_id: `region_${region.name.toLowerCase().replace(/\s+/g, '_')}`,
    name: region.name,
    formatted_address: region.name,
    description: region.description,
    is_region: true,
    region_type: 'popular'
  })
}

// Select a suggestion
const selectSuggestion = async (suggestion) => {
  query.value = suggestion.description || suggestion.name
  showSuggestions.value = false
  
  // If it's a predefined region, handle it directly
  if (suggestion.is_region) {
    emit('select', {
      place_id: suggestion.place_id,
      name: suggestion.name,
      formatted_address: suggestion.name,
      description: suggestion.structured_formatting?.secondary_text,
      is_region: true,
      region_type: suggestion.region_type,
      original_suggestion: suggestion
    })
    return
  }
  
  loading.value = true

  try {
    // Use the new Place API instead of deprecated PlacesService
    const place = new google.maps.places.Place({
      id: suggestion.place_id,
      requestedLanguage: 'en'
    })

    // Fetch detailed place information using new API
    await place.fetchFields({
      fields: [
        'id',
        'displayName',
        'formattedAddress',
        'location',
        'types',
        'addressComponents',
        'photos',
        'rating',
        'userRatingCount',
        'websiteURI',
        'internationalPhoneNumber'
      ]
    })

    loading.value = false
    
    // Create a new session token for the next search
    sessionToken = new google.maps.places.AutocompleteSessionToken()
    
    // Emit the select event with full place details using new API format
    emit('select', {
      place_id: place.id,
      name: place.displayName,
      formatted_address: place.formattedAddress,
      geometry: {
        location: {
          lat: () => place.location.lat,
          lng: () => place.location.lng
        }
      },
      types: place.types,
      address_components: place.addressComponents,
      photos: place.photos,
      rating: place.rating,
      user_ratings_total: place.userRatingCount,
      website: place.websiteURI,
      international_phone_number: place.internationalPhoneNumber,
      original_suggestion: suggestion
    })
  } catch (error) {
    loading.value = false
    console.error('Error fetching place details with new Place API:', error)
    
    // Create a new session token for the next search
    sessionToken = new google.maps.places.AutocompleteSessionToken()
    
    // Still emit basic info if details fetch fails
    emit('select', {
      place_id: suggestion.place_id,
      name: suggestion.structured_formatting?.main_text || suggestion.description,
      formatted_address: suggestion.description,
      original_suggestion: suggestion
    })
  }
}

// Clear location input
const clearLocation = () => {
  query.value = ''
  suggestions.value = []
  showSuggestions.value = false
  
  emit('select', {
    place_id: null,
    name: '',
    formatted_address: '',
    cleared: true
  })
}

// Handle focus event
const handleFocus = () => {
  showSuggestions.value = true
  // If field is empty, show popular regions
  if (!query.value.trim()) {
    // Popular regions are shown via template logic
    console.log('Showing popular regions on focus')
  }
}

// Focus input (for pin icon click when empty)
const focusInput = () => {
  if (inputRef.value) {
    inputRef.value.focus()
  }
}

// Hide suggestions with delay to allow click events
const hideSuggestions = () => {
  setTimeout(() => {
    showSuggestions.value = false
  }, 150)
}

// Initialize on component mount
onMounted(async () => {
  try {
    // Load Google Maps API using the loader utility
    await loadGoogleMapsAPI()
    console.log('Google Maps API loaded for InternationalLocation')
    initServices()
  } catch (error) {
    console.error('Failed to load Google Maps API in InternationalLocation:', error)
  }
  
  // Add resize listener
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  // Remove resize listener
  window.removeEventListener('resize', onResize)
})

// Expose methods for parent component if needed
defineExpose({
  clearInput: () => {
    clearLocation()
  },
  setQuery: (value) => {
    query.value = value
  }
})
</script>

<style scoped>
/* Ensure standard mobile input size to prevent iOS zoom (mobile only) */
@media (max-width: 767px) {
  input[type="text"] {
    font-size: 16px; /* iOS zoom prevention */
    -webkit-appearance: none;
    appearance: none;
  }
  input::placeholder {
    font-weight: 400; /* normal weight for placeholder on mobile */
  }
}
</style>
